export type FilterOption = { value: string; label: string };

export function distinct(values: (string | undefined)[]): string[] {
	return [...new Set(values.filter((value): value is string => !!value))];
}

type FilterableNgo = { countryCode?: string; region?: string };

/**
 * Region names arrive already resolved from GeoNames (`forms/regions`), and Italy's carry their legal
 * form in front of the name: "Regione Lombardia", "Regione Autonoma Siciliana". Nobody picking a
 * filter is looking for the statute — every Italian option read as "Regione …" and the actual region
 * started 8 or 17 characters in — so the prefix comes off. No other country's file prefixes its
 * regions, which is why this is anchored on the word rather than applied per country.
 */
const REGION_PREFIX = /^Regione\s+(?:Autonoma\s+)?(?:dell[a']\s*)?/i;

/**
 * Dropping the prefix off the two whose name declines with it leaves an adjective — "Regione Autonoma
 * Siciliana" becomes "Siciliana" — so those are named outright. The key is what the regex leaves, not
 * the raw name, so it stays right if the prefix ever changes.
 */
const REGION_ALIASES: Record<string, string> = { Siciliana: 'Sicilia' };

/** The region as the dropdown should print it. The stored name stays the filter's value. */
export function regionLabel(name: string): string {
	const stripped = name.replace(REGION_PREFIX, '');
	return REGION_ALIASES[stripped] ?? stripped;
}

export function regionOptionsFor(ngos: FilterableNgo[], country: string | null): FilterOption[] {
	const inCountry = country ? ngos.filter((ngo) => ngo.countryCode === country) : ngos;
	return distinct(inCountry.map((ngo) => ngo.region))
		.map((value) => ({ value, label: regionLabel(value) }))
		.sort((a, b) => a.label.localeCompare(b.label));
}
